// web/src/components/follow/FollowButtonV3.tsx
import React, { useEffect, useState, useCallback } from "react";
import { auth, db } from "@/lib/firebase";
import { toast } from "@/components/ui/use-toast";
import {
  getDoc,
  doc,
  serverTimestamp,
  writeBatch,
  addDoc,
  collection,
} from "firebase/firestore";
import { Button } from "@/components/ui/button";

type Privacy = "open" | "request" | "private";
type EdgeStatusInternal = "none" | "pending" | "active"; // 'active' == approved

type Props = {
  targetUid: string;
  targetPrivacy?: Privacy | null;
  targetUsername?: string | null;
  targetAvatarUrl?: string | null;
  className?: string;
  sizePx?: number;
  hideIfOwn?: boolean;
  labelVariant?: "icon" | "text";
};

export default function FollowButtonV3({
  targetUid,
  targetPrivacy = "open",
  targetUsername,
  targetAvatarUrl,
  className = "",
  sizePx = 48,
  hideIfOwn = true,
  labelVariant = "icon",
}: Props) {
  const me = auth.currentUser?.uid || null;
  const [status, setStatus] = useState<EdgeStatusInternal>("none");
  const [theyFollowMe, setTheyFollowMe] = useState(false);
  const [busy, setBusy] = useState(false);

  // Hide on own profile if requested
  if (hideIfOwn && me && me === targetUid) return null;

  // Load my outgoing edge (hierarchical)
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!me || !targetUid || me === targetUid) {
        if (alive) setStatus("none");
        return;
      }
      try {
        const snap = await getDoc(doc(db, "follows", me, "following", targetUid));
        if (!alive) return;
        const v = snap.exists() ? (snap.data() as any) : null;
        setStatus(
          v?.status === "active" || v?.status === "pending" ? v.status : "none"
        );
      } catch {
        if (alive) setStatus("none");
      }
    })();
    return () => {
      alive = false;
    };
  }, [me, targetUid]);

  // Load their relationship to me (mutual check) – broaden to legacy & shadow paths
  useEffect(() => {
    let alive = true;
    async function checkMutual() {
      if (!me || !targetUid || me === targetUid) {
        if (alive) setTheyFollowMe(false);
        return;
      }
      const approvals = new Set<string>(["active", "approved"]);
      try {
        const hier = getDoc(doc(db, "follows", targetUid, "following", me));
        const shadow = getDoc(doc(db, "users", targetUid, "following", me));
        const flat = getDoc(doc(db, "follows", `${targetUid}_${me}`));
        const [h, s, f] = await Promise.allSettled([hier, shadow, flat]);
        let mutual = false;
        const tryDoc = (r: PromiseSettledResult<any>) => {
          if (r.status !== "fulfilled") return;
            const snap = r.value;
            if (snap?.exists()) {
              const data: any = snap.data();
              if (data?.status && approvals.has(String(data.status))) mutual = true;
              else if (
                data?.followerUid === targetUid &&
                data?.targetUid === me &&
                approvals.has(String(data.status))
              )
                mutual = true;
              else if (
                data?.followerId === targetUid &&
                data?.followingId === me &&
                approvals.has(String(data.status))
              )
                mutual = true;
            }
        };
        tryDoc(h);
        tryDoc(s);
        tryDoc(f);
        if (alive) setTheyFollowMe(mutual);
      } catch {
        if (alive) setTheyFollowMe(false);
      }
    }
    void checkMutual();
  }, [me, targetUid, status, db]);

  const enqueueNotification = async (toUid: string, data: any) => {
    try {
      await addDoc(collection(db, "users", toUid, "notifications"), {
        ...data,
        createdAt: serverTimestamp(),
        channels: ["email"],
        status: "queued",
      });
    } catch {
      /* ignore */
    }
  };

  const writeOpenFollow = useCallback(async () => {
    if (!me) {
      toast({ description: "Sign in to follow users." });
      return;
    }
    if (me === targetUid) return;
    setBusy(true);
    try {
      const now = serverTimestamp();
      const followingRef = doc(db, "follows", me, "following", targetUid);
      const followerRef = doc(db, "follows", targetUid, "followers", me);
      const shadowFollowingRef = doc(db, "users", me, "following", targetUid);
      const shadowFollowerRef = doc(db, "users", targetUid, "followers", me);

      const batch = writeBatch(db);
      const core = {
        fromUid: me,
        toUid: targetUid,
        followerUid: me,
        targetUid,
        status: "active",
        statusText: "approved",
        event: "follow_approved",
        createdAt: now,
        updatedAt: now,
      };
      batch.set(followingRef, core, { merge: true });
      batch.set(followerRef, core, { merge: true });
      batch.set(shadowFollowingRef, core, { merge: true });
      batch.set(shadowFollowerRef, core, { merge: true });
      await batch.commit();

      await enqueueNotification(targetUid, {
        type: "follow",
        subtype: "new_follower",
        event: "follow_approved",
        fromUid: me,
        toUid: targetUid,
        followerUid: me,
        targetUid,
      });

      setStatus("active");
      toast({
        description: `Following${
          targetUsername ? ` @${targetUsername}` : ""
        }`,
      });
    } catch (e: any) {
      toast({ description: e?.message || "Could not follow." });
    } finally {
      setBusy(false);
    }
  }, [me, targetUid, targetUsername]);

  const writeRequestFollow = useCallback(async () => {
    if (!me) {
      toast({ description: "Sign in to follow users." });
      return;
    }
    if (me === targetUid) return;
    setBusy(true);
    try {
      const now = serverTimestamp();
      const followingRef = doc(db, "follows", me, "following", targetUid);
      const followerRef = doc(db, "follows", targetUid, "followers", me);
      const reqRef = doc(db, "followRequests", targetUid, "incoming", me);
      const shadowFollowingRef = doc(db, "users", me, "following", targetUid);
      const shadowFollowerRef = doc(db, "users", targetUid, "followers", me);

      const pendingFollowing = {
        fromUid: me,
        toUid: targetUid,
        followerUid: me,
        targetUid,
        status: "pending",
        statusText: "pending",
        event: "follow_request",
        createdAt: now,
        updatedAt: now,
      };
      const pendingFollower = pendingFollowing;

      batch:
      {
        const batch = writeBatch(db);
        batch.set(followingRef, pendingFollowing, { merge: true });
        batch.set(followerRef, pendingFollower, { merge: true });
        batch.set(reqRef, pendingFollower, { merge: true });
        batch.set(shadowFollowingRef, pendingFollowing, { merge: true });
        batch.set(shadowFollowerRef, pendingFollower, { merge: true });
        await batch.commit();
      }

      await enqueueNotification(targetUid, {
        type: "follow",
        subtype: "request",
        event: "follow_request",
        fromUid: me,
        toUid: targetUid,
        followerUid: me,
        targetUid,
      });

      setStatus("pending");
      toast({
        description: `Follow request sent${
          targetUsername ? ` to @${targetUsername}` : ""
        }`,
      });
    } catch (e: any) {
      toast({ description: e?.message || "Could not send request." });
    } finally {
      setBusy(false);
    }
  }, [me, targetUid, targetUsername]);

  const writeUnfollow = useCallback(async () => {
    if (!me) return;
    setBusy(true);
    try {
      const followingRef = doc(db, "follows", me, "following", targetUid);
      const followerRef = doc(db, "follows", targetUid, "followers", me);
      const reqRef = doc(db, "followRequests", targetUid, "incoming", me);
      const shadowFollowingRef = doc(db, "users", me, "following", targetUid);
      const shadowFollowerRef = doc(db, "users", targetUid, "followers", me);

      const batch = writeBatch(db);
      batch.delete(followingRef);
      batch.delete(followerRef);
      batch.delete(reqRef);
      batch.delete(shadowFollowingRef);
      batch.delete(shadowFollowerRef);
      await batch.commit();
      setStatus("none");
      toast({
        description: `Unfollowed${
          targetUsername ? ` @${targetUsername}` : ""
        }`,
      });
    } catch (e: any) {
      toast({ description: e?.message || "Could not unfollow." });
    } finally {
      setBusy(false);
    }
  }, [me, targetUid, targetUsername]);

  // Click logic
  const handleToggle = useCallback(async () => {
    if (!auth.currentUser) {
      toast({ description: "Sign in to follow users." });
      return;
    }
    if (me === targetUid) return;
    if (status === "active") {
      await writeUnfollow();
      return;
    }
    if (status === "pending") {
      await writeUnfollow();
      return;
    }
    if (targetPrivacy === "open") await writeOpenFollow();
    else await writeRequestFollow();
  }, [
    status,
    targetPrivacy,
    writeOpenFollow,
    writeRequestFollow,
    writeUnfollow,
    me,
    targetUid,
  ]);

  // Mapping for PNG styles (normalize internal -> display)
  const displayStatus = status === "active" ? "approved" : status; // approved/pending/none
  const isMutual = displayStatus === "approved" && theyFollowMe;

  // Choose PNG sources (adjust paths to your actual asset location)
  const src =
    displayStatus === "pending"
      ? "/images/pending.png"
      : displayStatus === "approved"
      ? isMutual
        ? "/images/mutual.png"
        : "/images/following.png"
      : "/images/follow.png";

  const aria =
    displayStatus === "pending"
      ? "Requested"
      : displayStatus === "approved"
      ? isMutual
        ? "Following (mutual)"
        : "Following"
      : "Follow";

  const textLabel =
    displayStatus === "pending"
      ? "Requested"
      : displayStatus === "approved"
      ? isMutual
        ? "Mutual"
        : "Following"
      : targetPrivacy === "open"
      ? "Follow"
      : "Request";

  if (labelVariant === "text") {
    return (
      <Button
        type="button"
        className={className}
        disabled={busy}
        onClick={handleToggle}
        aria-label={aria}
        title={aria}
      >
        {busy ? "Please wait…" : textLabel}
      </Button>
    );
  }

  // Icon (PNG) variant
  return (
    <button
      type="button"
      aria-label={aria}
      title={aria}
      disabled={busy}
      onClick={handleToggle}
      className={[
        "p-0 bg-transparent border-0 hover:bg-transparent focus:ring-0 shadow-none",
        className,
        busy && "opacity-60 cursor-wait",
      ]
        .filter(Boolean)
        .join(" ")}
      style={{ lineHeight: 0 }}
    >
      <img
        src={src}
        alt={aria}
        width={sizePx}
        height={sizePx}
        className="block select-none"
        draggable={false}
        style={{ opacity: busy ? 0.6 : 1 }}
      />
    </button>
  );
}

